import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {getWaterQualityRecords, createWaterQualityRecord, updateWaterQualityRecord} from "../../api/pond";
import {useTranslation} from "react-i18next";

interface MeasurementTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
}

const MeasurementTab: React.FC<MeasurementTabProps> = ({pondId, cycleId, isCycleActive = true}) => {
  const {t} = useTranslation();
  const [measurements, setMeasurements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [formData, setFormData] = useState({
    recordDate: "",
    temperature: 0,
    ph: 0,
    dissolvedOxygen: 0,
    turbidity: 0,
    ammonia: 0,
    nitrite: 0,
    nitrate: 0,
    salinity: 0,
    transparency: 0,
    orp: 0,
    notes: "",
  });

  useEffect(() => {
    fetchMeasurements();
  }, [pondId, cycleId]);

  const fetchMeasurements = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getWaterQualityRecords(pondId, cycleId);
      setMeasurements(data.data || data);
    } catch (err) {
      setError("Failed to load water quality records");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        recordDate: record.recordDate ? new Date(record.recordDate).toISOString().slice(0, 16) : "",
        temperature: record.temperature,
        ph: record.ph,
        dissolvedOxygen: record.dissolvedOxygen,
        turbidity: record.turbidity || 0,
        ammonia: record.ammonia || 0,
        nitrite: record.nitrite || 0,
        nitrate: record.nitrate || 0,
        salinity: record.salinity || 0,
        transparency: record.transparency || 0,
        orp: record.orp || 0,
        notes: record.notes || "",
      });
    } else {
      setEditingRecord(null);
      setFormData({
        recordDate: new Date().toISOString().slice(0, 16),
        temperature: 0,
        ph: 0,
        dissolvedOxygen: 0,
        turbidity: 0,
        ammonia: 0,
        nitrite: 0,
        nitrate: 0,
        salinity: 0,
        transparency: 0,
        orp: 0,
        notes: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSave = async () => {
    try {
      if (editingRecord) {
        await updateWaterQualityRecord(editingRecord.id, formData);
      } else {
        await createWaterQualityRecord(pondId, formData);
      }
      handleCloseModal();
      fetchMeasurements();
    } catch (err) {
      setError("Failed to save water quality record");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("Water Quality Measurements")}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()} disabled={!isCycleActive}>
          {t("Add Measurement")}
        </Button>
      </Box>
      {!isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}
      <TableContainer component={Paper} sx={{overflowX: "auto", maxWidth: "100%"}}>
        <Table size="small" sx={{minWidth: 700}}>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Time")}</TableCell>
              <TableCell>{t("Temperature (°C)")}</TableCell>
              <TableCell>{t("pH")}</TableCell>
              <TableCell>{t("DO (mg/L)")}</TableCell>
              <TableCell>{t("Ammonia (mg/L)")}</TableCell>
              <TableCell>{t("Nitrite (mg/L)")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {measurements.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.recordDate ? new Date(record.recordDate).toLocaleDateString() : "-"}</TableCell>
                <TableCell>{record.recordTime || "-"}</TableCell>
                <TableCell>{record.temperature}</TableCell>
                <TableCell>{record.ph}</TableCell>
                <TableCell>{record.dissolvedOxygen}</TableCell>
                <TableCell>{record.ammonia}</TableCell>
                <TableCell>{record.nitrite}</TableCell>
                <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{record.remark || ""}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => handleOpenModal(record)} disabled={!isCycleActive}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Water Quality Record") : t("Add Water Quality Record")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t("Date & Time")}
              type="datetime-local"
              value={formData.recordDate}
              onChange={(e) => setFormData({...formData, recordDate: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <Box display="flex" gap={2}>
              <TextField
                label={t("Temperature (°C)")}
                type="number"
                value={formData.temperature}
                onChange={(e) => setFormData({...formData, temperature: parseFloat(e.target.value)})}
                fullWidth
              />
              <TextField
                label={t("pH")}
                type="number"
                value={formData.ph}
                onChange={(e) => setFormData({...formData, ph: parseFloat(e.target.value)})}
                fullWidth
              />
              <TextField
                label={t("Dissolved Oxygen (mg/L)")}
                type="number"
                value={formData.dissolvedOxygen}
                onChange={(e) => setFormData({...formData, dissolvedOxygen: parseFloat(e.target.value)})}
                fullWidth
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label={t("Salinity (ppt)")}
                type="number"
                value={formData.salinity}
                onChange={(e) => setFormData({...formData, salinity: parseFloat(e.target.value)})}
                fullWidth
              />
              <TextField
                label={t("Transparency (cm)")}
                type="number"
                value={formData.transparency}
                onChange={(e) => setFormData({...formData, transparency: parseFloat(e.target.value)})}
                fullWidth
              />
              <TextField
                label={t("ORP (mV)")}
                type="number"
                value={formData.orp}
                onChange={(e) => setFormData({...formData, orp: parseFloat(e.target.value)})}
                fullWidth
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label={t("Turbidity (NTU)")}
                type="number"
                value={formData.turbidity}
                onChange={(e) => setFormData({...formData, turbidity: parseFloat(e.target.value)})}
                fullWidth
              />
              <TextField
                label={t("Ammonia (mg/L)")}
                type="number"
                value={formData.ammonia}
                onChange={(e) => setFormData({...formData, ammonia: parseFloat(e.target.value)})}
                fullWidth
              />
              <TextField
                label={t("Nitrite (mg/L)")}
                type="number"
                value={formData.nitrite}
                onChange={(e) => setFormData({...formData, nitrite: parseFloat(e.target.value)})}
                fullWidth
              />
            </Box>
            <TextField
              label={t("Nitrate (mg/L)")}
              type="number"
              value={formData.nitrate}
              onChange={(e) => setFormData({...formData, nitrate: parseFloat(e.target.value)})}
            />
            <TextField
              label={t("Notes")}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={handleSave} variant="contained">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeasurementTab;
